import { Card } from "@design";
import { useMutation } from "@tanstack/react-query";
import { BookOpen, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import {
  type ConnectorTemplateResponse,
  createConnectorFromTemplateMutation,
  listConnectorsQueryKey,
  listSkillsQueryKey,
} from "@/api";
import { EntityFormCard } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { CopyValue } from "@/components/CopyValue";
import { useAppForm } from "@/components/form/form";
import { InlineNotice } from "@/components/InlineNotice";
import { Text } from "@/components/Text";
import { HelpMarkdown } from "@/features/help/HelpMarkdown";
import { useHelpArticles } from "@/features/help/useHelpArticles";

/** The template's setup guide (a Help article), folded under the form's intro. */
function SetupGuide({ slug }: Readonly<{ slug: string }>) {
  const { t } = useTranslation("connectors");
  const [open, setOpen] = useState(true);
  const article = useHelpArticles().find((a) => a.slug === slug);
  if (!article) return null;
  return (
    <Card size="sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 text-left btn-focus-ring"
      >
        <BookOpen size={16} aria-hidden className="text-content-tertiary" />
        <Text as="span" variant="body" weight="semibold" className="flex-1">
          {t("template.guide")}
        </Text>
        <ChevronDown
          size={16}
          aria-hidden
          className={`text-content-tertiary transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && (
        <div className="mt-3 max-h-96 overflow-y-auto">
          <HelpMarkdown>{article.body}</HelpMarkdown>
        </div>
      )}
    </Card>
  );
}

/** A pattern from the server, or null if it isn't a valid regular expression (then it isn't checked). */
function safePattern(pattern: string | null | undefined): RegExp | null {
  if (!pattern) return null;
  try {
    return new RegExp(`^(?:${pattern})$`);
  } catch {
    return null;
  }
}

function schemaFor(template: ConnectorTemplateResponse, t: (key: string, options?: object) => string) {
  const patterns = template.secretPatterns.map(safePattern).filter((p): p is RegExp => p !== null);
  const fieldShape = Object.fromEntries(
    (template.fields ?? []).map((f) => [
      f.name,
      z
        .string()
        .trim()
        .min(1, t("template.fieldRequired", { label: f.label }))
        .refine((v) => safePattern(f.pattern)?.test(v) ?? true, f.hint || t("template.fieldInvalid")),
    ]),
  );
  const oauth = template.authMode === "oauth";
  const clientId = safePattern(template.clientIdPattern);
  return z.object({
    name: z.string().trim().min(1, t("instance.nameRequired")).max(255),
    clientId: oauth
      ? z
          .string()
          .trim()
          .min(1, t("template.clientIdRequired"))
          .refine((v) => !clientId || clientId.test(v), t("template.clientIdInvalid", { hint: template.clientIdHint }))
      : z.string(),
    secret: z
      .string()
      .trim()
      .min(1, t("instance.secretRequired", { label: template.secretLabel }))
      .refine(
        (v) => patterns.length === 0 || patterns.some((p) => p.test(v)),
        t("template.secretInvalid", { hint: template.secretHint }),
      ),
    fields: z.object(fieldShape),
    readOnly: z.boolean(),
    starterSkill: z.boolean(),
  });
}

/** Connect a known server (GitHub, Linear...) in one step: its guide, your key and a few values (MHS-875). */
export function TemplateConnectorForm({ template }: Readonly<{ template: ConnectorTemplateResponse }>) {
  const { t } = useTranslation("connectors");
  const navigate = useNavigate();
  const { createNew } = useEntitySave({
    saved: t("template.created"),
    saveFailed: t("template.failed"),
    created: t("template.created"),
    createFailed: t("template.failed"),
  });
  const create = useMutation(createConnectorFromTemplateMutation());
  const tt = t as unknown as (key: string, options?: object) => string;
  const oauth = template.authMode === "oauth";

  const form = useAppForm({
    defaultValues: {
      name: template.name,
      clientId: "",
      secret: "",
      fields: Object.fromEntries((template.fields ?? []).map((f) => [f.name, ""])) as Record<string, string>,
      readOnly: template.readOnly,
      starterSkill: template.hasStarterSkill,
    },
    validators: { onChange: schemaFor(template, tt) },
    onSubmit: ({ value }) =>
      createNew({
        request: async () =>
          (
            await create.mutateAsync({
              body: {
                template: template.slug,
                name: value.name.trim(),
                secret: value.secret.trim(),
                ...(oauth ? { clientId: value.clientId.trim() } : {}),
                fields: Object.fromEntries(Object.entries(value.fields).map(([k, v]) => [k, v.trim()])),
                readOnly: value.readOnly,
                starterSkill: value.starterSkill,
              },
              throwOnError: true,
            })
          ).data.connector.uuid,
        invalidate: [listConnectorsQueryKey(), listSkillsQueryKey()],
        to: (uuid) => `/build/connectors/${uuid}`,
      }),
  });

  return (
    <div className="flex flex-col gap-4">
      {template.integrationName && (
        <InlineNotice>{t("template.integrationNote", { integration: template.integrationName })}</InlineNotice>
      )}
      <SetupGuide slug={template.helpSlug} />
      <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
        <Text variant="body-sm" tone="secondary">
          {template.description}
        </Text>
        <form.AppField name="name">{(field) => <field.TextField label={t("form.name")} required />}</form.AppField>
        {(template.fields ?? []).map((f) => (
          <form.AppField key={f.name} name={`fields.${f.name}`}>
            {(field) => <field.TextField label={f.label} required placeholder={f.placeholder} />}
          </form.AppField>
        ))}
        {oauth && (
          <>
            {template.oauthRedirectUri && (
              <div className="flex flex-col gap-1">
                <Text variant="body-sm">{t("template.redirectUri")}</Text>
                <CopyValue value={template.oauthRedirectUri} label={t("signIn.redirectUriLabel")} />
              </div>
            )}
            <form.AppField name="clientId">
              {(field) => <field.TextField label={t("template.clientId")} required />}
            </form.AppField>
          </>
        )}
        <form.AppField name="secret">
          {(field) => <field.SecretField label={capitalize(template.secretLabel)} required />}
        </form.AppField>
        <Text variant="caption" tone="tertiary">
          {oauth ? t("template.signInHint") : t("instance.secretHint")}
        </Text>
        <form.AppField name="readOnly">{(field) => <field.ToggleField label={t("template.readOnly")} />}</form.AppField>
        <Text variant="caption" tone="tertiary">
          {t("template.readOnlyHint")}
        </Text>
        {template.hasStarterSkill && (
          <>
            <form.AppField name="starterSkill">
              {(field) => <field.ToggleField label={t("template.starterSkill")} />}
            </form.AppField>
            <Text variant="caption" tone="tertiary">
              {t("template.starterSkillHint")}
            </Text>
          </>
        )}
        <form.AppForm>
          <form.SubmitActions
            onCancel={() => navigate("/build/connectors")}
            submitLabel={t("form.create")}
            requireChanges={false}
          />
        </form.AppForm>
      </EntityFormCard>
    </div>
  );
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
